import React, {useEffect, useState, useRef} from 'react';
import axios from 'axios';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import Card from 'react-bootstrap/Card';
import {useStoreActions, useStoreState} from 'easy-peasy';
import SmartFilters from './SmartFilters';
import i18n from 'i18n';
import {buildStoryboardURL} from '@/utils/urlBuilder';

interface GlobalFilters {
    allFields: Record<string, any>;
    additionalFilters: Record<string, any>;
}

interface Storyboard {
    id: number;
    writeAccess: boolean;
    globalFilters: {
        all_fields: Record<string, any>;
        additional_filters: Record<string, any>;
    };
}

interface StoreState {
    search: {
        searchQueryAllFields: Record<string, any>;
        searchQueryAdditionalFilters: Record<string, any>;
    };
    activeStoryboard: {
        activeId: number | null;
        activeStoryboard: Storyboard | null;
    };
}

interface StoreActions {
    storyboards: {
        setStoryboard: (payload: { storyboardId: number; storyboardData: any }) => void;
    };
}

const StoryboardFilters: React.FC = React.memo(() => {
    const searchQueryAllFields = useStoreState((state: StoreState) => state.search.searchQueryAllFields);
    const searchQueryAdditionalFilters = useStoreState((state: StoreState) => state.search.searchQueryAdditionalFilters);

    const activeStoryboardId = useStoreState((state: StoreState) => state.activeStoryboard.activeId);
    const activeStoryboard = useStoreState((state: StoreState) => state.activeStoryboard.activeStoryboard);

    const setStoryboard = useStoreActions((actions: StoreActions) => actions.storyboards.setStoryboard);

    const unsavedFiltersTextDefault: string = i18n.t('jt_all_filters_saved.text');
    const [unsavedFiltersText, setUnsavedFiltersText] = useState<string>(unsavedFiltersTextDefault);
    const [savingFilters, setSavingFilters] = useState<boolean>(false);
    const [enableReinitializeDefault, setEnableReinitializeDefault] = useState<boolean>(false);
    const activeStoryboardIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (activeStoryboardIdRef.current !== null) {
            setEnableReinitializeDefault(true);
        }
        activeStoryboardIdRef.current = activeStoryboardId;
    }, [activeStoryboardId]);

    useEffect(() => {
        if (activeStoryboard?.writeAccess) {
            let unsavedCount = 0;
            if (Object.keys(activeStoryboard?.globalFilters).length !== 0) {
                unsavedCount += Object.keys(activeStoryboard?.globalFilters?.additional_filters).length;
                unsavedCount += Object.keys(activeStoryboard?.globalFilters?.all_fields).length;
            }
            if (searchQueryAdditionalFilters) {
                unsavedCount -= Object.keys(searchQueryAdditionalFilters).length;
            }
            if (searchQueryAllFields) {
                unsavedCount -= Object.keys(searchQueryAllFields).length;
            }
            if (unsavedCount !== 0) {
                setUnsavedFiltersText(`${Math.abs(unsavedCount)} ${i18n.t('jt_unsaved_filters_changes.text')}!`);
            } else {
                setUnsavedFiltersText(unsavedFiltersTextDefault);
            }
        }
    }, [
        activeStoryboardId,
        searchQueryAllFields,
        searchQueryAdditionalFilters,
        activeStoryboard?.globalFilters?.all_fields,
    ]);

    const saveGlobalFilters = async (): Promise<void> => {
        if (!activeStoryboardId) {return;}

        setSavingFilters(true);
        const response = await axios.patch(buildStoryboardURL('storyboardDetails', activeStoryboardId), {
            globalFilters: {
                allFields: searchQueryAllFields,
                additionalFilters: searchQueryAdditionalFilters,
            },
        });
        setStoryboard({storyboardId: response.data.id, storyboardData: response.data});
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('all_fields');
        searchParams.delete('additionalFilters');
        const nextSearch = searchParams.toString();
        window.history.replaceState(null, document.title, nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname);
        setSavingFilters(false);
        setUnsavedFiltersText(i18n.t('jt_all_filters_saved.text'));
    };

    const haveWriteAccess: boolean = activeStoryboard?.writeAccess || false;

    return (
        <Card id="filters-card">
            <Card.Body>
                <div className="r-panel-body">
                    <SmartFilters
                        alwaysUpdateReduxQueryParams
                        widgetsResetKey={String(activeStoryboard?.id)}
                        enableReinitializeDefault={enableReinitializeDefault}
                        saveButtonContent={
                            haveWriteAccess ? (
                                <div id="save-filters-btn">
                                    <Button
                                        variant="info"
                                        size="sm"
                                        onClick={saveGlobalFilters}
                                        disabled={savingFilters}
                                        data-cy="storyboard-filter-sidebar-save-filters-button"
                                    >
                                        {
                                            savingFilters ? (
                                                <Spinner
                                                    className="mr-2"
                                                    variant="border"
                                                    size="sm"
                                                />
                                            ) : null
                                        }
                                        {i18n.t('jt_save_filters.text')}
                                    </Button>
                                    <span className="text-muted ml-2">
                                        {unsavedFiltersText}
                                    </span>
                                </div>
                            ) : undefined
                        }
                    />
                </div>
            </Card.Body>
        </Card>
    );
});

export default StoryboardFilters; 